// screens/RecordBrowse.tsx — C1 (2026-09-28): axis chips (kind · month ·
// para · type · media); the bucket list with counts; a bucket opens its
// items in the activity row shape. Empty states state the truth — Browse →
// Project (para axis) says plainly that no project pages exist yet rather
// than showing a fake list (spec, and `projects` carrying 0 rows today).
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getRecordBrowseBuckets,
  getRecordBrowseItems,
  type RecordActivityItemApi,
  type RecordBrowseAxisApi,
  type RecordBrowseBucketApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import './spread.css';
import './RecordHome.css'; // shares .record__strip/.record__strip-btn with the home's filter strip
import './RecordBrowse.css';

export interface RecordBrowseItemTarget {
  kind: RecordActivityItemApi['kind'];
  id: string;
  slug?: string | null;
}

export interface RecordBrowseProps {
  tenantId: string;
  axis: string;
  value?: string;
  onBack: () => void;
  onOpenBrowse: (axis: string, value?: string) => void;
  onOpenItem: (item: RecordBrowseItemTarget) => void;
}

const AXES: ReadonlyArray<{ axis: RecordBrowseAxisApi; label: string }> = [
  { axis: 'kind', label: 'Kind' },
  { axis: 'month', label: 'Month' },
  { axis: 'para', label: 'Project · Area · Resource · Archive' },
  { axis: 'note_type', label: 'Type' },
  { axis: 'media_class', label: 'Media' },
];

const VALID_AXES = new Set(AXES.map((a) => a.axis));

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'buckets'; buckets: RecordBrowseBucketApi[] }
  | { phase: 'items'; items: RecordActivityItemApi[] };

const KIND_LABEL: Record<string, string> = { note: 'page', file: 'file', decision: 'decision', radar: 'radar' };

export function RecordBrowse({ tenantId, axis, value, onBack, onOpenBrowse, onOpenItem }: RecordBrowseProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });

  useEffect(() => {
    setLoad({ phase: 'loading' });
    if (!VALID_AXES.has(axis as RecordBrowseAxisApi)) {
      setLoad({ phase: 'error', message: `"${axis}" is not a browse axis` });
      return;
    }
    const safeAxis = axis as RecordBrowseAxisApi;
    if (value) {
      getRecordBrowseItems(tenantId, safeAxis, value)
        .then((items) => setLoad({ phase: 'items', items }))
        .catch((err: unknown) => setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the record is not reachable' }));
    } else {
      getRecordBrowseBuckets(tenantId, safeAxis)
        .then((buckets) => setLoad({ phase: 'buckets', buckets }))
        .catch((err: unknown) => setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the record is not reachable' }));
    }
  }, [tenantId, axis, value]);

  function openItem(item: RecordActivityItemApi) {
    onOpenItem({ kind: item.kind, id: item.id, slug: item.slug });
  }

  return (
    <div class="sp">
      <BackLink label="Record" onClick={onBack} />
      <h1 class="sp__title">Browse</h1>

      <div class="record__strip">
        {AXES.map((a) => (
          <button
            type="button"
            key={a.axis}
            class={`record__strip-btn${a.axis === axis ? ' record__strip-btn--active' : ''}`}
            onClick={() => onOpenBrowse(a.axis)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {load.phase === 'loading' ? (
        <div class="sp__say">One moment…</div>
      ) : load.phase === 'error' ? (
        <div class="sp__say">
          The record is not reachable.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      ) : load.phase === 'buckets' ? (
        load.buckets.length === 0 ? (
          <div class="sp__empty">Nothing to browse here yet.</div>
        ) : (
          load.buckets.map((b) => (
            <button type="button" class="sp__row" key={b.value} onClick={() => onOpenBrowse(axis, b.value)}>
              <span class="sp__row-top">
                <span class="sp__row-name">{b.label}</span>
                <span class="sp__chip">{b.count}</span>
              </span>
            </button>
          ))
        )
      ) : load.items.length === 0 ? (
        <div class="sp__empty">
          {axis === 'para' && value === 'project' ? 'No project pages exist yet.' : 'Nothing here yet.'}
        </div>
      ) : (
        load.items.map((item) => (
          <button type="button" class="sp__row" key={`${item.kind}-${item.id}`} onClick={() => openItem(item)}>
            <span class="sp__row-top">
              <span class="sp__row-name">{item.title}</span>
              <span class="sp__chip">{KIND_LABEL[item.kind] ?? item.kind}</span>
            </span>
            <span class="sp__row-sub">
              by {item.actor} · {item.what}
            </span>
          </button>
        ))
      )}
    </div>
  );
}
